import type { ReactNode } from "react";

/**
 * Renders the legal documents in docs/legal (headings, paragraphs, lists, tables, quotes, bold, links) as React
 * elements. The text has one source: the documents the association reviews and approves. No HTML injection.
 */
const LINKS: Record<string, string> = {
  "privacy-policy.md": "/privacy",
  "terms-of-use.md": "/terms",
};

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={k++}>{inline(m[1])}</strong>);
    else if (m[2] !== undefined) {
      const href = LINKS[m[3]!] ?? (/^https:\/\//.test(m[3]!) ? m[3]! : null);
      out.push(
        href ? (
          <a key={k++} href={href}>
            {m[2]}
          </a>
        ) : (
          m[2]
        ),
      );
    } else out.push(<code key={k++}>{m[4]}</code>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) =>
  row
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((c) => c.trim());

function blocks(lines: string[]): ReactNode[] {
  const out: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
    } else if (/^#{1,3} /.test(line)) {
      const level = line.match(/^#+/)![0].length;
      const text = inline(line.replace(/^#+ /, ""));
      out.push(
        level === 1 ? (
          <h1 key={k++}>{text}</h1>
        ) : level === 2 ? (
          <h2 key={k++}>{text}</h2>
        ) : (
          <h3 key={k++}>{text}</h3>
        ),
      );
      i++;
    } else if (line.startsWith(">")) {
      const quoted: string[] = [];
      while (i < lines.length && lines[i]!.startsWith(">")) quoted.push(lines[i++]!.replace(/^> ?/, ""));
      out.push(
        <aside key={k++} className="legal-note">
          {blocks(quoted)}
        </aside>,
      );
    } else if (line.startsWith("|")) {
      const rows: string[] = [];
      while (i < lines.length && lines[i]!.startsWith("|")) rows.push(lines[i++]!);
      const [head, , ...body] = rows;
      out.push(
        <div key={k++} className="table-wrap">
          <table>
            <thead>
              <tr>
                {cells(head!).map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, n) => (
                <tr key={n}>
                  {cells(r).map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
    } else if (/^\s*(- |\d+\. )/.test(line)) {
      const ordered = /^\s*\d+\. /.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*(- |\d+\. )/.test(lines[i]!)) {
        items.push(lines[i++]!.replace(/^\s*(- |\d+\. )/, ""));
      }
      const li = items.map((t, j) => <li key={j}>{inline(t)}</li>);
      out.push(ordered ? <ol key={k++}>{li}</ol> : <ul key={k++}>{li}</ul>);
    } else {
      const para: string[] = [];
      while (i < lines.length && lines[i]!.trim() && !/^(#|>|\||\s*- |\s*\d+\. )/.test(lines[i]!))
        para.push(lines[i++]!);
      out.push(<p key={k++}>{inline(para.join(" "))}</p>);
    }
  }
  return out;
}

export function Markdown({ source }: { source: string }) {
  return <div className="legal">{blocks(source.split("\n"))}</div>;
}
